/**
 * Firebase Phone Authentication Service (Web)
 * Handles phone number OTP authentication for web app
 */

import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  signOut as firebaseSignOut,
  User
} from 'firebase/auth';
import auth from '../config/firebase';

export class FirebasePhoneAuthWeb {
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private confirmationResult: ConfirmationResult | null = null;
  private recaptchaWidgetId: number | null = null;

  /**
   * Initialize reCAPTCHA verifier
   * @param containerElementId - ID of the HTML element for reCAPTCHA
   * @param isInvisible - Whether to use invisible reCAPTCHA
   * @returns Promise with success status
   */
  async initRecaptcha(
    containerElementId: string = 'recaptcha-container',
    isInvisible: boolean = false
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log('🔐 Initializing reCAPTCHA...');

      // Check if container exists
      const container = document.getElementById(containerElementId);
      if (!container) {
        return {
          success: false,
          message: `reCAPTCHA container '${containerElementId}' not found in DOM`
        };
      }

      // Clear existing reCAPTCHA if any
      if (this.recaptchaVerifier) {
        this.recaptchaVerifier.clear();
      }

      // Create new reCAPTCHA verifier
      // Note: Using 'invisible' to avoid rendering issues with Firebase reCAPTCHA Enterprise
      this.recaptchaVerifier = new RecaptchaVerifier(auth, containerElementId, {
        size: 'invisible',
        callback: () => {
          console.log('✅ reCAPTCHA verified');
        },
        'expired-callback': () => {
          console.warn('⚠️ reCAPTCHA expired');
        }
      });

      // Render reCAPTCHA - wrapped in try/catch to handle initialization errors
      try {
        this.recaptchaWidgetId = await this.recaptchaVerifier.render();
      } catch (renderError: any) {
        console.warn('⚠️ reCAPTCHA render failed, but may still work:', renderError.code);
        // Don't throw - reCAPTCHA may still function even if render reports error
      }

      console.log('✅ reCAPTCHA initialized successfully');
      console.log('Widget ID:', this.recaptchaWidgetId);

      return {
        success: true,
        message: 'reCAPTCHA initialized successfully'
      };

    } catch (error: any) {
      console.error('❌ reCAPTCHA initialization error:', error);
      return {
        success: false,
        message: error.message || 'Failed to initialize reCAPTCHA'
      };
    }
  }

  /**
   * Send OTP to phone number
   * @param phoneNumber - Phone number in E.164 format (e.g., +919876543210)
   * @returns Promise with success status and message
   */
  async sendOTP(phoneNumber: string): Promise<{
    success: boolean;
    message: string;
    verificationId?: string;
  }> {
    try {
      console.log('📱 Firebase Phone Auth: Sending OTP...');

      // Validate phone number format
      if (!phoneNumber.startsWith('+')) {
        return {
          success: false,
          message: 'Phone number must include country code (e.g., +919876543210)'
        };
      }

      // Basic E.164 validation
      const e164Regex = /^\+[1-9]\d{1,14}$/;
      if (!e164Regex.test(phoneNumber)) {
        return {
          success: false,
          message: 'Invalid phone number format. Use +[country code][number]'
        };
      }

      // Check if reCAPTCHA is initialized
      if (!this.recaptchaVerifier) {
        return {
          success: false,
          message: 'reCAPTCHA not initialized. Call initRecaptcha() first.'
        };
      }

      // Send verification code via Firebase
      this.confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        this.recaptchaVerifier
      );

      console.log('✅ OTP sent successfully to:', this.maskPhone(phoneNumber));
      console.log('Verification ID:', this.confirmationResult.verificationId);

      return {
        success: true,
        message: 'OTP sent successfully! Check your phone.',
        verificationId: this.confirmationResult.verificationId
      };

    } catch (error: any) {
      console.error('❌ Firebase Phone Auth error:', error);

      // Reset reCAPTCHA on error
      if (this.recaptchaVerifier) {
        try {
          this.recaptchaVerifier.clear();
          // Re-render reCAPTCHA
          const container = document.querySelector('[id^="recaptcha-container"]');
          if (container) {
            this.recaptchaWidgetId = await this.recaptchaVerifier.render();
          }
        } catch (resetError) {
          console.error('Error resetting reCAPTCHA:', resetError);
        }
      }

      // Handle specific Firebase errors
      if (error.code === 'auth/invalid-phone-number') {
        return {
          success: false,
          message: 'Invalid phone number. Please check and try again.'
        };
      } else if (error.code === 'auth/too-many-requests') {
        return {
          success: false,
          message: 'Too many requests. Please wait a few minutes and try again.'
        };
      } else if (error.code === 'auth/quota-exceeded') {
        return {
          success: false,
          message: 'Daily SMS quota exceeded. Please try again tomorrow.'
        };
      } else if (error.code === 'auth/network-request-failed') {
        return {
          success: false,
          message: 'Network error. Please check your connection and try again.'
        };
      } else if (error.code === 'auth/captcha-check-failed') {
        return {
          success: false,
          message: 'reCAPTCHA verification failed. Please try again.'
        };
      }

      return {
        success: false,
        message: error.message || 'Failed to send OTP. Please try again.'
      };
    }
  }

  /**
   * Verify OTP code
   * @param code - 6-digit OTP code received via SMS
   * @returns Promise with verification result
   */
  async verifyOTP(code: string): Promise<{
    success: boolean;
    message: string;
    idToken?: string;
    phoneNumber?: string;
    userId?: string;
    isNewUser?: boolean;
  }> {
    try {
      console.log('🔐 Firebase Phone Auth: Verifying OTP...');

      if (!this.confirmationResult) {
        return {
          success: false,
          message: 'No OTP request found. Please request OTP first.'
        };
      }

      // Verify the code with Firebase
      const userCredential = await this.confirmationResult.confirm(code);

      console.log('✅ Phone number verified successfully!');
      console.log('User ID:', userCredential.user.uid);
      console.log('Phone:', this.maskPhone(userCredential.user.phoneNumber || ''));

      // Get Firebase ID token to send to backend
      const idToken = await userCredential.user.getIdToken();

      // Check if this is a new user (first time phone auth)
      const isNewUser = userCredential.user.metadata.creationTime ===
                        userCredential.user.metadata.lastSignInTime;

      return {
        success: true,
        message: 'Phone number verified successfully!',
        idToken,
        phoneNumber: userCredential.user.phoneNumber || undefined,
        userId: userCredential.user.uid,
        isNewUser
      };

    } catch (error: any) {
      console.error('❌ OTP verification error:', error);

      // Handle specific errors
      if (error.code === 'auth/invalid-verification-code') {
        return {
          success: false,
          message: 'Invalid OTP code. Please check and try again.'
        };
      } else if (error.code === 'auth/code-expired') {
        return {
          success: false,
          message: 'OTP code has expired. Please request a new one.'
        };
      } else if (error.code === 'auth/session-expired') {
        return {
          success: false,
          message: 'Session expired. Please request a new OTP.'
        };
      }

      return {
        success: false,
        message: 'Failed to verify OTP. Please try again.'
      };
    }
  }

  /**
   * Resend OTP to the same phone number
   * @param phoneNumber - Phone number to resend OTP to
   * @returns Promise with success status
   */
  async resendOTP(phoneNumber: string): Promise<{ success: boolean; message: string }> {
    console.log('🔄 Resending OTP...');

    // Clear previous confirmation
    this.confirmationResult = null;

    // Reset reCAPTCHA
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      const container = document.querySelector('[id^="recaptcha-container"]');
      if (container) {
        this.recaptchaWidgetId = await this.recaptchaVerifier.render();
      }
    }

    // Send new OTP
    return await this.sendOTP(phoneNumber);
  }

  /**
   * Sign out from Firebase Auth
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
      this.confirmationResult = null;
      console.log('✅ Signed out from Firebase Auth');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }

  /**
   * Get current Firebase user
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Clean up reCAPTCHA
   */
  cleanupRecaptcha(): void {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
      this.recaptchaWidgetId = null;
      console.log('✅ reCAPTCHA cleaned up');
    }
  }

  /**
   * Mask phone number for security (logging)
   * @param phone - Phone number to mask
   * @returns Masked phone number
   */
  private maskPhone(phone: string): string {
    if (!phone || phone.length < 4) return '****';
    return phone.substring(0, 4) + '****' + phone.substring(phone.length - 2);
  }

  /**
   * Format phone number to E.164
   * @param phone - Phone number to format
   * @param defaultCountryCode - Default country code if not provided
   * @returns Formatted phone number
   */
  formatPhoneNumber(phone: string, defaultCountryCode: string = '+91'): string {
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');

    // If doesn't start with +, add default country code
    if (!cleaned.startsWith('+')) {
      // Remove leading 0 if present (common in India)
      if (cleaned.startsWith('0')) {
        cleaned = cleaned.substring(1);
      }
      cleaned = defaultCountryCode + cleaned;
    }

    return cleaned;
  }
}

// Export singleton instance
export const firebasePhoneAuth = new FirebasePhoneAuthWeb();
